import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PhotoAIField from "../components/PhotoAIField";
import CustomerHandlePicker from "../components/CustomerHandlePicker";
import PaymentMethodMenu, { METHOD_LABEL } from "../components/PaymentMethodMenu";
import { brl, fmtDate, normalizeHandle } from "../lib/format";
import { toast } from "sonner";
import {
  Plus, Trash, ArrowLeft, Check, ClipboardText, Copy, UploadSimple, ArrowsClockwise, SquaresFour,
  ChatCircleDots, Receipt, Bank, CheckCircle, Warning, X as XIcon, Package, PencilSimple, FloppyDisk,
  FilePdf, FileXls, Camera, Sparkle, XCircle,
} from "@phosphor-icons/react";
import MoneyInput, { parseMoney } from "../components/MoneyInput";
import LazyImage from "../components/LazyImage";
import WithdrawReasonModal from "../components/WithdrawReasonModal";
import { useAuth } from "../context/AuthContext";
import LiveGallery from "../components/LiveGallery";
import EditLiveModal from "../components/EditLiveModal";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function LiveDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm, user } = useAuth();
  const isContractor = !!user?.contractor_id;
  const canWrite = hasPerm("lives.write");
  const canReconcile = hasPerm("reconcile.write");
  const [tab, setTab] = useState("orders");
  const [editingLive, setEditingLive] = useState(false);
  const [live, setLive] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [txs, setTxs] = useState([]);

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      api.get(`/lives/${id}`),
      api.get(`/lives/${id}/orders`),
      api.get(`/products?slim=1`),
      api.get(`/customers?slim=1`),
      api.get(`/departments`),
      api.get(`/lives/${id}/messages`),
      api.get(`/lives/${id}/transactions`),
    ]);
    const val = (i) => (results[i].status === "fulfilled" ? results[i].value.data : (Array.isArray(results[i]?.value?.data) ? [] : null));
    setLive(val(0));
    setOrders(val(1) || []);
    setProducts(val(2) || []);
    setCustomers(val(3) || []);
    setDepartments(val(4) || []);
    setMessages(val(5) || []);
    setTxs(val(6) || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refreshAfterMutation = async () => {
    const results = await Promise.allSettled([
      api.get(`/lives/${id}/orders`),
      api.get(`/lives/${id}/messages`),
      api.get(`/lives/${id}/transactions`),
    ]);
    if (results[0].status === "fulfilled") setOrders(results[0].value.data);
    if (results[1].status === "fulfilled") setMessages(results[1].value.data);
    if (results[2].status === "fulfilled") setTxs(results[2].value.data);
  };

  const refreshProducts = async () => {
    const { data } = await api.get(`/products`);
    setProducts(data);
  };

  const refreshCustomers = async () => {
    const { data } = await api.get(`/customers?slim=1`);
    setCustomers(data);
  };

  if (!live) return <div className="p-8 font-bold">Carregando…</div>;
  const liveProducts = live.contractor_id
    ? products.filter(p => p.contractor_id === live.contractor_id)
    : products.filter(p => !p.contractor_id || !p.is_sold);


  const total = orders.reduce((s, o) => s + o.price * (o.quantity || 1), 0);
  const paid = orders.filter(o => o.paid).reduce((s, o) => s + o.price * (o.quantity || 1), 0);
  const withdrawn = orders.filter(o => o.withdrawn).reduce((s, o) => s + o.price * (o.quantity || 1), 0);
  const pending = total - paid - withdrawn;

  const downloadReport = async (kind /* 'pdf' | 'xlsx' | 'photos' */) => {
    try {
      const path = kind === "photos" ? "report-photos.pdf" : `report.${kind}`;
      // Cache-buster + explicit no-cache headers force a fresh render (needed because
      // browsers may otherwise reuse a stale PDF after users delete/edit orders).
      const resp = await api.get(`/lives/${id}/${path}`, {
        responseType: "blob",
        params: { _: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      const blob = new Blob([resp.data], {
        type: kind === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const safe = (live.name || "live").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "photos" ? `vendas_fotos_${safe}_${live.date || ""}.pdf` : `live_${safe}_${live.date || ""}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório ${kind.toUpperCase()} baixado`);
    } catch {
      toast.error(`Falha ao gerar ${kind.toUpperCase()}`);
    }
  };

  return (
    <div data-testid="live-detail-page" className="space-y-6">
      {editingLive && <EditLiveModal live={live} onClose={() => setEditingLive(false)} onSaved={() => { setEditingLive(false); load(); }} />}
      <button data-testid="back-to-lives" onClick={() => nav("/lives")} className="inline-flex items-center gap-2 font-bold text-sm hover:underline">
        <ArrowLeft weight="bold" size={16} /> Voltar
      </button>

      <header className="card-brutal p-4 sm:p-6 bg-brand-pink text-white">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest">{fmtDate(live.date)}</p>
            <h1 className="font-heading text-3xl sm:text-5xl font-black">{live.name}</h1>
            {live.contractor && (
              <p className="mt-2"><span className="badge-brutal !bg-brand-yellow !text-brand-ink">Contratante: {live.contractor.name}</span></p>
            )}
            {!isContractor && live.commission_pct != null && (
              <p className="mt-2"><span data-testid="live-commission-badge" className="badge-brutal !bg-white !text-brand-ink">Loja: {live.commission_pct}%</span></p>
            )}
            {live.note && <p className="text-white/80 mt-1">{live.note}</p>}
            {!isContractor && <div className="mt-3 flex flex-wrap gap-2">
              {user?.can_edit_live && (
                <button
                  data-testid="edit-live-btn"
                  onClick={() => setEditingLive(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-brand-yellow text-brand-ink border-2 border-brand-ink font-bold text-xs uppercase tracking-wide hover:bg-white transition"
                >
                  <PencilSimple weight="bold" size={16} /> Editar live
                </button>
              )}
              <button
                data-testid="export-live-pdf-btn"
                onClick={() => downloadReport("pdf")}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white text-brand-ink border-2 border-brand-ink font-bold text-xs uppercase tracking-wide hover:bg-brand-yellow transition"
              >
                <FilePdf weight="bold" size={16} /> Exportar PDF
              </button>
              <button
                data-testid="export-live-photos-pdf-btn"
                onClick={() => downloadReport("photos")}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white text-brand-ink border-2 border-brand-ink font-bold text-xs uppercase tracking-wide hover:bg-brand-yellow transition"
              >
                <Camera weight="bold" size={16} /> Relação de vendas com fotos
              </button>
              <button
                data-testid="export-live-xlsx-btn"
                onClick={() => downloadReport("xlsx")}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white text-brand-ink border-2 border-brand-ink font-bold text-xs uppercase tracking-wide hover:bg-brand-mint transition"
              >
                <FileXls weight="bold" size={16} /> Exportar Excel
              </button>
            </div>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatBox label="Total" value={brl(total)} bg="bg-brand-ink" text="text-white" />
            <StatBox label="Pago" value={brl(paid)} bg="bg-brand-success" text="text-white" />
            <StatBox label="Desistências" value={brl(withdrawn)} bg="bg-brand-peach" text="text-brand-ink" />
            <StatBox label="A receber" value={brl(pending)} bg="bg-brand-yellow" text="text-brand-ink" />
          </div>
        </div>
      </header>

      <div className="flex gap-2 border-b-2 border-brand-ink overflow-x-auto -mx-1 px-1">
        <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={Receipt} label="Pedidos" testid="tab-orders" />
        {isContractor && <TabBtn active={tab === "gallery"} onClick={() => setTab("gallery")} icon={SquaresFour} label="Galeria" testid="tab-gallery" />}
        {!isContractor && <>
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} icon={Package} label="Produtos" testid="tab-products" />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={ChatCircleDots} label="Cobrança" testid="tab-messages" />
          <TabBtn active={tab === "reconcile"} onClick={() => setTab("reconcile")} icon={Bank} label="Conciliação" testid="tab-reconcile" />
        </>}
      </div>

      {tab === "orders" && (
        <OrdersTab
          liveId={id}
          orders={orders}
          products={liveProducts}
          customers={customers}
          onChange={refreshAfterMutation}
          onCustomersChanged={refreshCustomers}
          onOpenReconcile={() => setTab("reconcile")}
          canWrite={canWrite}
        />
      )}
      {tab === "products" && (
        <LiveProductsTab
          scopeContractor={live.contractor ? { id: live.contractor_id, name: live.contractor.name } : null}
          products={liveProducts}
          departments={departments}
          onChange={refreshProducts}
          canWrite={canWrite}
        />
      )}
      {tab === "gallery" && isContractor && <LiveGallery orders={orders} />}
      {tab === "messages" && <MessagesTab messages={messages} />}
      {tab === "reconcile" && (
        <ReconcileTab liveId={id} txs={txs} orders={orders} onChange={refreshAfterMutation} canWrite={canReconcile} />
      )}
    </div>
  );
}

function StatBox({ label, value, bg, text = "text-brand-ink" }) {
  return (
    <div className={`${bg} ${text} border-2 border-brand-ink px-2 py-2 text-center min-w-0`}>
      <p className="text-[10px] font-bold uppercase opacity-90">{label}</p>
      <p className="font-heading text-sm sm:text-lg font-black leading-tight break-words">{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 sm:px-4 py-3 font-bold text-xs sm:text-sm uppercase tracking-wide border-2 border-b-0 -mb-[2px] whitespace-nowrap ${
        active ? "bg-white border-brand-ink" : "bg-transparent border-transparent text-brand-ink2 hover:text-brand-ink"
      }`}
    >
      <Icon weight="bold" size={16} /> {label}
    </button>
  );
}

/* -------- Orders Tab -------- */
function OrdersTab({ liveId, orders, products, customers, onChange, onCustomersChanged, onOpenReconcile, canWrite }) {
  const [form, setForm] = useState({ handle: "", product_code: "", description: "", price: "", quantity: 1, photo_url: "" });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // Synchronous barrier: setState is async, so this guards same-microtask double-clicks
  const handleInputRef = useRef(null);
  const [blocklist, setBlocklist] = useState({}); // { "@handle": {count, total, reasons} }

  useEffect(() => {
    let alive = true;
    api.get("/customers/blocklist")
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        for (const b of data || []) {
          map[(b.handle || "").toLowerCase()] = b;
        }
        setBlocklist(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const currentBlock = blocklist[normalizeHandle(form.handle || "").toLowerCase()] || null;

  const groupedByHandle = useMemo(() => {
    const g = {};
    for (const o of orders) (g[o.handle] ||= []).push(o);
    return g;
  }, [orders]);

  const groupEntries = useMemo(() => {
    return Object.entries(groupedByHandle)
      .map(([handle, items]) => {
        const total = items.reduce((s, o) => s + o.price * (o.quantity || 1), 0);
        const paid = items.filter(o => o.paid).reduce((s, o) => s + o.price * (o.quantity || 1), 0);
        const allPaid = items.every(o => o.paid);
        const anyWithdrawn = items.some(o => o.withdrawn);
        const allWithdrawn = items.every(o => o.withdrawn);
        const pendingOnly = items.filter(o => !o.paid && !o.withdrawn);
        return { handle, items, total, paid, allPaid, anyWithdrawn, allWithdrawn, pendingOnly };
      })
      .sort((a, b) => a.handle.localeCompare(b.handle));
  }, [groupedByHandle]);

  const onProductBlurOrChange = (code) => {
    const p = products.find(pr => pr.code.toLowerCase() === code.trim().toLowerCase());
    if (p) setForm(f => ({ ...f, product_code: p.code, description: p.description, price: String(p.price).replace(".", ",") }));
    else setForm(f => ({ ...f, product_code: code }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return; // Synchronous guard against same-microtask rapid clicks
    if (!form.handle.trim() || !form.product_code.trim() || !form.description.trim()) {
      return toast.error("Preencha arroba, código e descrição");
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await api.post("/orders", {
        live_id: liveId,
        handle: normalizeHandle(form.handle),
        product_code: form.product_code.trim(),
        description: form.description.trim(),
        price: parseMoney(form.price),
        quantity: parseInt(form.quantity) || 1,
        photo_url: form.photo_url || null,
      });
      // Keep same handle for quick sequential orders from same client
      setForm(f => ({ ...f, product_code: "", description: "", price: "", quantity: 1, photo_url: "" }));
      handleInputRef.current?.focus();
      onChange();
    } catch { toast.error("Erro ao salvar pedido"); }
    finally { savingRef.current = false; setSaving(false); }
  };

  const togglePaid = async (o, method) => {
    // When marking as paid (o.paid === false), method is required
    const patch = { paid: !o.paid };
    if (!o.paid && (method === "pix" || method === "credit")) patch.payment_method = method;
    await api.put(`/orders/${o.id}`, patch);
    onChange();
  };

  const markGroupPaid = async (handle, allPaid, method) => {
    const nextPaid = !allPaid;
    if (nextPaid && !window.confirm(`Marcar TODOS os itens de ${handle} como pagos via ${METHOD_LABEL[method] || method}?`)) return;
    if (!nextPaid && !window.confirm(`Desmarcar TODOS os itens de ${handle}?`)) return;
    try {
      await api.post(`/lives/${liveId}/handles/${encodeURIComponent(handle)}/mark-paid`,
        nextPaid ? { paid: true, payment_method: method } : { paid: false },
      );
      toast.success(nextPaid ? `Todos pagos via ${METHOD_LABEL[method]}` : "Itens desmarcados");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const [withdrawFor, setWithdrawFor] = useState(null); // { handle, total, items_count, reason }
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const openWithdraw = (payload) => setWithdrawFor({ ...payload, reason: "" });
  const closeWithdraw = () => { if (!withdrawBusy) setWithdrawFor(null); };
  const markGroupWithdrawn = async () => {
    if (!withdrawFor) return;
    const { handle, reason } = withdrawFor;
    const trimmed = (reason || "").trim();
    setWithdrawBusy(true);
    try {
      const { data } = await api.post("/reports/pending-by-handle/mark-paid", {
        handle,
        payment_method: "withdrawn",
        live_ids: [liveId],
        reason: trimmed || undefined,
      });
      toast.success(`${data.modified} item(ns) de ${handle} marcados como desistência${trimmed ? ` — ${trimmed}` : ""}`);
      setWithdrawFor(null);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar desistência");
    } finally {
      setWithdrawBusy(false);
    }
  };

  const revertGroupWithdrawn = async (handle) => {
    if (!window.confirm(`Reverter desistência(s) de ${handle} nesta live? Voltará para A Receber.`)) return;
    try {
      const { data } = await api.post("/reports/pending-by-handle/revert-withdrawn", {
        handle, live_ids: [liveId],
      });
      toast.success(`${data.modified} item(ns) voltaram para A Receber`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reverter");
    }
  };

  // Load pending link requests for this live and index by handle to show "Gerar Link" flag
  const [pendingLinkByHandle, setPendingLinkByHandle] = useState({});
  const [pendingReceiptsByHandle, setPendingReceiptsByHandle] = useState({});
  const loadLinkRequests = useCallback(async () => {
    if (!canWrite) return;
    try {
      const [lr, rc] = await Promise.all([
        api.get(`/lives/${liveId}/link-requests`, { params: { _: Date.now() } }),
        api.get(`/lives/${liveId}/receipts`, { params: { _: Date.now() } }),
      ]);
      const map = {};
      for (const r of (lr.data || [])) {
        if (r.status === "pending") (map[r.handle] = map[r.handle] || []).push(r);
      }
      setPendingLinkByHandle(map);
      const rmap = {};
      for (const r of (rc.data || [])) {
        if (r.status === "submitted") (rmap[r.handle] = rmap[r.handle] || []).push(r);
      }
      setPendingReceiptsByHandle(rmap);
    } catch (err) {
      console.warn("Failed to load link requests / receipts for live:", liveId, err);
    }
  }, [liveId, canWrite]);
  useEffect(() => { loadLinkRequests(); }, [loadLinkRequests, orders]);

  const remove = async (o) => {
    if (!window.confirm("Remover este pedido?")) return;
    await api.delete(`/orders/${o.id}`);
    onChange();
  };

  return (
    <div className="space-y-6">
      {!canWrite && (
        <div className="card-brutal p-3 bg-brand-yellow flex items-center gap-2 text-sm font-bold">
          <Warning weight="bold" size={16} /> Visualização somente leitura — você tem acesso de contratante.
        </div>
      )}
      {/* Quick form */}
      {canWrite && (
      <form onSubmit={submit} className="card-brutal p-5 space-y-3 bg-brand-yellow">
        <div className="flex items-center gap-2">
          <Plus weight="bold" size={18} />
          <h3 className="font-heading text-lg font-black uppercase tracking-wide">Registro rápido</h3>
        </div>
        <PhotoAIField
          photoUrl={form.photo_url}
          onPhoto={(dataUrl) => setForm(f => ({ ...f, photo_url: dataUrl }))}
          onExtract={(fields) => setForm(f => ({
            ...f,
            description: fields.description || f.description,
            product_code: f.product_code || fields.suggested_code || "",
            price: f.price || (fields.estimated_price != null ? String(fields.estimated_price).replace(".", ",") : ""),
          }))}
          endpoint="/orders/analyze-photo"
          testidPrefix="order"
          compact
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1.2fr_1fr_2fr_120px_80px_auto] gap-3">
          <div className="sm:col-span-2 md:col-span-1">
            <CustomerHandlePicker
              testid="order-handle"
              inputRef={handleInputRef}
              value={form.handle}
              onChange={(v) => setForm(f => ({ ...f, handle: v }))}
              customers={customers}
              onCustomerCreated={onCustomersChanged}
              autoFocus
            />
            {currentBlock && (
              <div
                data-testid="order-handle-blocklist-warning"
                className="mt-1 text-[11px] card-brutal !p-2 bg-brand-peach flex items-start gap-1"
              >
                <XCircle weight="bold" size={14} className="mt-[1px] flex-shrink-0" />
                <div>
                  <p className="font-bold uppercase tracking-wider text-[10px]">Atenção — cliente com histórico</p>
                  <p>
                    <strong>{currentBlock.count}</strong> desistência(s) · {brl(currentBlock.total || 0)}
                    {currentBlock.reasons?.length ? <> · <em>{currentBlock.reasons.join(" · ")}</em></> : null}
                  </p>
                </div>
              </div>
            )}
          </div>

          <input
            data-testid="order-code-input"
            list="product-codes"
            className="input-brutal"
            placeholder="Código"
            value={form.product_code}
            onChange={(e) => onProductBlurOrChange(e.target.value)}
          />
          <datalist id="product-codes">
            {products.map(p => <option key={p.id} value={p.code}>{p.description} · {brl(p.price)}</option>)}
          </datalist>

          <input
            data-testid="order-description-input"
            className="input-brutal sm:col-span-2 md:col-span-1"
            placeholder="Descrição"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <MoneyInput
            data-testid="order-price-input"
            className="input-brutal"
            placeholder="Preço"
            value={form.price}
            onChange={(v) => setForm({ ...form, price: v })}
          />
          <input
            data-testid="order-qty-input"
            type="number" min="1"
            className="input-brutal"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          />
          <button data-testid="submit-order-btn" type="submit" disabled={saving} className="btn-pink disabled:opacity-60 disabled:cursor-not-allowed sm:col-span-2 md:col-span-1 h-11">
            {saving ? <ArrowsClockwise weight="bold" size={16} className="animate-spin" /> : <Plus weight="bold" size={16} />}
          </button>
        </div>
      </form>
      )}

      {/* Grouped list */}
      {orders.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Receipt weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhum pedido ainda</p>
          <p className="text-brand-ink2 text-sm">Comece registrando o primeiro pedido acima.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupEntries.map(({ handle, items, total: t, allPaid, anyWithdrawn, allWithdrawn, pendingOnly }) => {
            return (
              <div key={handle} data-testid={`orders-group-${handle}`} className="card-brutal overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 bg-brand-mint px-3 sm:px-4 py-3 border-b-2 border-brand-ink">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="font-heading text-base sm:text-lg font-black break-all">{handle}</span>
                    <span className="badge-brutal bg-white">{items.length} itens</span>
                    {allPaid ? (
                      <span className="badge-brutal bg-brand-success text-white">Pago</span>
                    ) : allWithdrawn ? (
                      <span className="badge-brutal bg-brand-peach text-brand-ink">Desistência</span>
                    ) : anyWithdrawn ? (
                      <span className="badge-brutal bg-brand-peach">Parcial · c/ desistência</span>
                    ) : (
                      <span className="badge-brutal bg-brand-peach">Pendente</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {canWrite && pendingReceiptsByHandle[handle]?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`group-view-receipt-${handle}`}
                        onClick={() => onOpenReconcile?.()}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-brand-ink font-bold text-xs uppercase tracking-wide bg-brand-yellow hover:bg-brand-ink hover:text-white animate-pulse"
                        title="A cliente enviou comprovante — clique para revisar e confirmar (baixa automática)"
                      >
                        <Receipt weight="bold" size={14} />
                        Ver comprovante
                        <span className="badge-brutal !bg-brand-pink !text-white !text-[10px] !py-0 !px-1.5">
                          {pendingReceiptsByHandle[handle].length}
                        </span>
                      </button>
                    )}
                    {canWrite && pendingLinkByHandle[handle]?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`group-gen-link-${handle}`}
                        onClick={() => onOpenReconcile?.()}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-brand-ink font-bold text-xs uppercase tracking-wide bg-brand-purple text-white hover:bg-brand-ink animate-pulse"
                        title="A cliente solicitou um link de pagamento — clique para gerar e enviar no direct"
                      >
                        <span aria-hidden>🔗</span>
                        Gerar Link de Crédito
                        <span className="badge-brutal !bg-brand-yellow !text-brand-ink !text-[10px] !py-0 !px-1.5">
                          {pendingLinkByHandle[handle].length}
                        </span>
                      </button>
                    )}
                    {canWrite && (
                      allPaid ? (
                        <button
                          type="button"
                          data-testid={`group-mark-paid-${handle}`}
                          onClick={() => markGroupPaid(handle, allPaid)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-brand-ink font-bold text-xs uppercase tracking-wide bg-white hover:bg-brand-peach"
                          title="Desmarcar todos os pedidos deste cliente"
                        >
                          <CheckCircle weight="bold" size={14} />
                          Desmarcar tudo
                        </button>
                      ) : (
                        <PaymentMethodMenu
                          testid={`group-pay-${handle}`}
                          label="Marcar tudo pago"
                          variant="green"
                          size="md"
                          onSelect={(m) => markGroupPaid(handle, allPaid, m)}
                        />
                      )
                    )}
                    {canWrite && !allPaid && pendingOnly.length > 0 && (
                      <button
                        type="button"
                        data-testid={`group-withdraw-${handle}`}
                        onClick={() => openWithdraw({ handle, items_count: pendingOnly.length, total: pendingOnly.reduce((s, o) => s + o.price * (o.quantity || 1), 0) })}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-brand-ink font-bold text-xs uppercase tracking-wide bg-brand-peach hover:bg-brand-pink hover:text-white"
                        title="Marcar pendentes como desistência/bloqueio"
                      >
                        <XIcon weight="bold" size={14} />
                        Desist./Bloqueio
                      </button>
                    )}
                    {canWrite && anyWithdrawn && (
                      <button
                        type="button"
                        data-testid={`group-revert-withdraw-${handle}`}
                        onClick={() => revertGroupWithdrawn(handle)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-brand-ink font-bold text-xs uppercase tracking-wide bg-white hover:bg-brand-mint"
                        title="Reverter desistência(s) desta @"
                      >
                        <ArrowsClockwise weight="bold" size={14} />
                        Reverter desist.
                      </button>
                    )}
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase text-brand-ink2">Total</p>
                      <p className="font-heading font-black">{brl(t)}</p>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="tbl w-full min-w-[720px]">
                  <thead>
                    <tr>
                      <th className="w-12">Foto</th>
                      <th className="w-24">Código</th>
                      <th>Descrição</th>
                      <th className="w-16 text-right">Qtd</th>
                      <th className="w-28 text-right">Preço</th>
                      <th className="w-28 text-right">Subtotal</th>
                      <th className="w-32 text-right">Status</th>
                      <th className="w-24 text-right">—</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(o => (
                      <OrderRow
                        key={o.id}
                        order={o}
                        canWrite={canWrite}
                        onTogglePaid={togglePaid}
                        onDelete={remove}
                        onSaved={onChange}
                      />
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {withdrawFor && (
        <WithdrawReasonModal
          data={{ handle: withdrawFor.handle, live_id: liveId, total: withdrawFor.total, reason: withdrawFor.reason }}
          onChange={(next) => setWithdrawFor((prev) => prev ? { ...prev, reason: next.reason } : prev)}
          onCancel={closeWithdraw}
          onConfirm={markGroupWithdrawn}
          busy={withdrawBusy}
        />
      )}
    </div>
  );
}

/* -------- Messages Tab -------- */
function MessagesTab({ messages }) {
  const copy = async (text, handle) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Mensagem para ${handle} copiada!`);
    } catch { toast.error("Não foi possível copiar"); }
  };

  if (messages.length === 0) {
    return (
      <div className="card-brutal p-10 text-center">
        <ChatCircleDots weight="bold" size={40} className="mx-auto" />
        <p className="font-heading text-xl font-black mt-3">Nenhuma mensagem para gerar</p>
        <p className="text-brand-ink2 text-sm">Adicione pedidos para gerar as cobranças.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {messages.map((m) => (
        <div key={m.handle} data-testid={`message-card-${m.handle}`} className="card-brutal p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-heading text-lg font-black">{m.handle}</p>
              <p className="text-xs text-brand-ink2">{m.items_count} itens · {brl(m.total)}</p>
            </div>
            <button
              data-testid={`copy-message-${m.handle}`}
              onClick={() => copy(m.message, m.handle)}
              className="btn-pink !px-3 !py-2"
            >
              <Copy weight="bold" size={16} /> Copiar
            </button>
          </div>
          <textarea
            data-testid={`message-textarea-${m.handle}`}
            readOnly
            value={m.message}
            className="input-brutal font-mono text-xs leading-relaxed h-56 resize-none"
          />
          {m.pending === 0 ? (
            <p className="text-sm mt-2"><span className="badge-brutal bg-brand-success text-white">Tudo pago</span></p>
          ) : (
            <p className="text-sm mt-2"><span className="badge-brutal bg-brand-peach">A receber: {brl(m.pending)}</span></p>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------- Reconcile Tab -------- */
function ReconcileTab({ liveId, txs, orders, onChange, canWrite }) {
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [pickingFor, setPickingFor] = useState(null); // tx being manually matched
  const [selected, setSelected] = useState({}); // orderId -> bool
  const [receipts, setReceipts] = useState([]);
  const [linkRequests, setLinkRequests] = useState([]);
  const [receiptLightbox, setReceiptLightbox] = useState(null);

  const loadReceipts = useCallback(async () => {
    try {
      const [rc, lr] = await Promise.all([
        api.get(`/lives/${liveId}/receipts`, { params: { _: Date.now() } }),
        api.get(`/lives/${liveId}/link-requests`, { params: { _: Date.now() } }),
      ]);
      setReceipts(rc.data);
      setLinkRequests(lr.data);
    } catch (err) {
      // Non-critical: reconciliation panels stay empty; still log for debugging.
      console.warn("Failed to load reconcile receipts/link-requests for live:", liveId, err);
    }
  }, [liveId]);
  useEffect(() => { loadReceipts(); }, [loadReceipts]);

  const updateReceiptStatus = async (r, status) => {
    let revert_orders = false;
    if (status === "rejected" && r.status === "confirmed") {
      // Ask the master whether to revert the orders previously auto-paid by this receipt
      revert_orders = window.confirm(
        `Este comprovante já estava CONFIRMADO. Quer também reverter os pedidos da ${r.handle} nesta live para PENDENTE?\n\n` +
        `OK = Rejeita + Reverte para pendente\nCancelar = Só rejeita (mantém os pedidos como pagos)`,
      );
    }
    try {
      const { data } = await api.put(`/receipts/${r.id}`, { status, revert_orders });
      if (status === "confirmed") {
        const n = data?.orders_paid || 0;
        toast.success(n > 0
          ? `Comprovante confirmado · ${n} pedido${n > 1 ? "s" : ""} da ${r.handle} marcado${n > 1 ? "s" : ""} como pago${n > 1 ? "s" : ""}`
          : "Comprovante confirmado");
        onChange();
      } else if (status === "rejected") {
        const n = data?.orders_unpaid || 0;
        toast.success(n > 0
          ? `Comprovante rejeitado · ${n} pedido${n > 1 ? "s" : ""} da ${r.handle} voltou para pendente`
          : "Comprovante rejeitado");
        if (n > 0) onChange();
      } else {
        toast.success("Atualizado");
      }
      loadReceipts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar comprovante");
    }
  };

  const updateLinkRequestStatus = async (r, status) => {
    try {
      await api.put(`/link-requests/${r.id}`, { status });
      toast.success(status === "sent" ? "Marcado como enviado" : status === "canceled" ? "Cancelado" : "Atualizado");
      loadReceipts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar solicitação");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/lives/${liveId}/upload-statement`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} lançamentos importados · ${data.matched} conciliados`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const runReconcile = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/lives/${liveId}/reconcile`);
      toast.success(`Conciliados: ${data.matched} · Ambíguos: ${data.ambiguous} · Sem match: ${data.unmatched}`);
      onChange();
    } finally { setRunning(false); }
  };

  const openPicker = (tx) => {
    setPickingFor(tx);
    setSelected({});
  };
  const closePicker = () => { setPickingFor(null); setSelected({}); };

  const confirmMatch = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error("Selecione pelo menos um pedido");
    await api.post(`/transactions/${pickingFor.id}/match`, ids);
    toast.success("Baixa registrada!");
    closePicker();
    onChange();
  };

  const remove = async (tx) => {
    if (!window.confirm("Remover este lançamento? Pedidos vinculados voltarão para pendente.")) return;
    await api.delete(`/transactions/${tx.id}`);
    onChange();
  };

  const unpaidOrders = orders.filter(o => !o.paid);
  const selectedSum = Object.entries(selected)
    .filter(([, v]) => v)
    .reduce((s, [oid]) => {
      const o = orders.find(x => x.id === oid);
      return s + (o ? o.price * (o.quantity || 1) : 0);
    }, 0);

  return (
    <div className="space-y-6">
      <div className="card-brutal p-6 bg-brand-mint">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl font-black flex items-center gap-2">
              <Bank weight="bold" size={22} /> Extrato bancário
            </h3>
            <p className="text-sm text-brand-ink2 mt-1">
              Envie o extrato em CSV ou OFX. Concilia automaticamente por nome (via cadastro de cliente) e valor.
            </p>
          </div>
          <div className="flex gap-2">
            <label className="btn-pink cursor-pointer">
              <UploadSimple weight="bold" size={16} />
              {uploading ? "Enviando…" : "Enviar extrato"}
              <input data-testid="upload-statement-input" type="file" accept=".csv,.ofx,.txt" hidden onChange={upload} disabled={uploading} />
            </label>
            <button data-testid="run-reconcile-btn" onClick={runReconcile} disabled={running} className="btn-white">
              <ArrowsClockwise weight="bold" size={16} /> Reprocessar
            </button>
          </div>
        </div>
      </div>

      {linkRequests.filter(r => r.status === "pending").length > 0 && (
        <div className="card-brutal overflow-hidden bg-brand-purple text-white">
          <div className="px-4 py-3 border-b-2 border-brand-ink">
            <h3 className="font-heading text-lg font-black flex items-center gap-2">
              🔗 Solicitações de link de pagamento
              <span className="badge-brutal !bg-brand-yellow !text-brand-ink">
                {linkRequests.filter(r => r.status === "pending").length}
              </span>
            </h3>
            <p className="text-xs opacity-80">Gere o link no seu gateway e envie no direct do Instagram. Depois marque como enviado.</p>
          </div>
          <div className="overflow-x-auto bg-white text-brand-ink">
            <table className="tbl w-full min-w-[680px]">
              <thead>
                <tr>
                  <th className="w-40">Cliente</th>
                  <th className="w-32">Solicitado em</th>
                  <th className="w-28 text-right">Valor</th>
                  <th>WhatsApp / Direct</th>
                  <th className="w-56 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {linkRequests.filter(r => r.status === "pending").map(r => (
                  <tr key={r.id} data-testid={`link-req-row-${r.id}`}>
                    <td>
                      <p className="font-bold text-sm">{r.handle}</p>
                      <p className="text-xs text-brand-ink2">{r.customer_name || "—"}</p>
                    </td>
                    <td className="text-xs">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                    <td className="text-right font-bold">{r.amount != null ? brl(r.amount) : "—"}</td>
                    <td className="text-xs">
                      {r.customer_whatsapp ? (
                        <a href={`https://wa.me/${r.customer_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="underline">
                          {r.customer_whatsapp}
                        </a>
                      ) : "—"}
                      <br />
                      <a href={`https://instagram.com/${r.handle.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="text-[11px] underline">
                        abrir Instagram
                      </a>
                    </td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        {canWrite ? (
                          <>
                            <button data-testid={`link-req-sent-${r.id}`}
                              onClick={() => updateLinkRequestStatus(r, "sent")}
                              className="btn-pink !h-9 !px-2 text-xs" title="Marcar como enviado no direct">
                              <Check weight="bold" size={14} /> Enviado
                            </button>
                            <button data-testid={`link-req-cancel-${r.id}`}
                              onClick={() => updateLinkRequestStatus(r, "canceled")}
                              className="btn-white !h-9 !px-2 text-xs !bg-brand-peach" title="Cancelar">
                              <XIcon weight="bold" size={14} />
                            </button>
                          </>
                        ) : (
                          <span className="text-[11px] text-brand-ink2 italic">Somente Master</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {receipts.length > 0 && (
        <div className="card-brutal overflow-hidden">
          <div className="px-4 py-3 bg-brand-yellow border-b-2 border-brand-ink">
            <h3 className="font-heading text-lg font-black flex items-center gap-2">
              <Receipt weight="bold" size={20} /> Comprovantes enviados pelas clientes
              <span className="badge-brutal !bg-white">{receipts.length}</span>
            </h3>
            <p className="text-xs text-brand-ink2">Confirme após bater com o extrato bancário para dar baixa manual nos itens.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="tbl w-full min-w-[720px]">
              <thead>
                <tr>
                  <th className="w-16">Recibo</th>
                  <th className="w-40">Cliente</th>
                  <th className="w-32">Enviado em</th>
                  <th className="w-28 text-right">Valor</th>
                  <th>Observação</th>
                  <th className="w-40">Status</th>
                  <th className="w-56 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map(r => {
                  const isPdf = (r.file_url || "").startsWith("data:application/pdf");
                  return (
                    <tr key={r.id} data-testid={`receipt-row-${r.id}`}>
                      <td>
                        {isPdf ? (
                          <a href={r.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold underline">
                            <FilePdf weight="bold" size={16} /> PDF
                          </a>
                        ) : (
                          <button type="button" onClick={() => setReceiptLightbox(r.file_url)}
                            className="w-12 h-12 border-2 border-brand-ink overflow-hidden hover:ring-2 hover:ring-brand-pink">
                            <img src={r.file_url} alt="" className="w-full h-full object-cover" />
                          </button>
                        )}
                      </td>
                      <td>
                        <p className="font-bold text-sm">{r.handle}</p>
                        <p className="text-xs text-brand-ink2">{r.customer_name || "—"}</p>
                      </td>
                      <td className="text-xs">{r.created_at ? new Date(r.created_at).toLocaleString("pt-BR") : "—"}</td>
                      <td className="text-right font-bold">{r.amount != null ? brl(r.amount) : "—"}</td>
                      <td className="text-xs">{r.note || "—"}</td>
                      <td>
                        <span className={`badge-brutal ${
                          r.status === "confirmed" ? "!bg-brand-success text-white" :
                          r.status === "rejected" ? "!bg-brand-peach" : "!bg-brand-yellow"
                        }`}>
                          {r.status === "confirmed" ? "Confirmado" : r.status === "rejected" ? "Rejeitado" : "Aguardando"}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="inline-flex gap-1">
                          {canWrite && r.status !== "confirmed" && (
                            <button data-testid={`receipt-confirm-${r.id}`}
                              onClick={() => updateReceiptStatus(r, "confirmed")}
                              className="btn-white !h-9 !px-2 text-xs" title="Confirmar recebimento e dar baixa nos pedidos">
                              <Check weight="bold" size={14} />
                            </button>
                          )}
                          {canWrite && r.status !== "rejected" && (
                            <button data-testid={`receipt-reject-${r.id}`}
                              onClick={() => updateReceiptStatus(r, "rejected")}
                              className="btn-white !h-9 !px-2 text-xs !bg-brand-peach" title="Rejeitar (com opção de reverter baixa)">
                              <XIcon weight="bold" size={14} />
                            </button>
                          )}
                          {!canWrite && (
                            <span className="text-[11px] text-brand-ink2 italic">Somente Master pode confirmar</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {receiptLightbox && (
        <div className="fixed inset-0 bg-brand-ink/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setReceiptLightbox(null)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setReceiptLightbox(null)}
              className="absolute -top-3 -right-3 bg-white border-2 border-brand-ink p-2 z-10">
              <XIcon weight="bold" size={16} />
            </button>
            <img src={receiptLightbox} alt="" className="max-w-[92vw] max-h-[85vh] object-contain border-4 border-brand-ink bg-white" />
          </div>
        </div>
      )}

      {txs.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Bank weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhum lançamento importado</p>
          <p className="text-brand-ink2 text-sm">Envie um extrato CSV/OFX para começar a conciliação.</p>
        </div>
      ) : (
        <div className="card-brutal overflow-hidden">
          <table className="tbl w-full">
            <thead>
              <tr>
                <th className="w-24">Data</th>
                <th>Pagador</th>
                <th className="w-28 text-right">Valor</th>
                <th className="w-40">Status</th>
                <th className="w-40 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {txs.map(tx => (
                <tr key={tx.id} data-testid={`tx-row-${tx.id}`}>
                  <td>{tx.date}</td>
                  <td>
                    <div className="font-bold">{tx.payer_name}</div>
                    {tx.matched_handle && <div className="text-xs text-brand-ink2">→ {tx.matched_handle}</div>}
                  </td>
                  <td className="text-right font-bold">{brl(tx.amount)}</td>
                  <td>
                    {tx.status === "matched" && <span className="badge-brutal bg-brand-success text-white"><CheckCircle weight="bold" size={12} /> Conciliado</span>}
                    {tx.status === "ambiguous" && <span className="badge-brutal bg-brand-yellow"><Warning weight="bold" size={12} /> Ambíguo</span>}
                    {tx.status === "unmatched" && <span className="badge-brutal bg-brand-peach">Sem match</span>}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      {tx.status !== "matched" && (
                        <button data-testid={`manual-match-${tx.id}`} onClick={() => openPicker(tx)} className="btn-white !px-2 !py-1 !text-xs">
                          Conciliar
                        </button>
                      )}
                      <button data-testid={`delete-tx-${tx.id}`} onClick={() => remove(tx)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                        <Trash weight="bold" size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual match modal */}
      {pickingFor && (
        <div className="fixed inset-0 bg-brand-ink/40 z-50 flex items-center justify-center p-4" onClick={closePicker}>
          <div className="card-brutal w-full max-w-2xl bg-white p-6 max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold uppercase text-brand-ink2">Conciliar lançamento</p>
                <p className="font-heading text-xl font-black">{pickingFor.payer_name} · {brl(pickingFor.amount)}</p>
              </div>
              <button onClick={closePicker} className="p-2 border-2 border-brand-ink"><XIcon weight="bold" size={16} /></button>
            </div>

            {unpaidOrders.length === 0 ? (
              <p className="text-brand-ink2">Não há pedidos pendentes.</p>
            ) : (
              <div className="space-y-2">
                {unpaidOrders.map(o => {
                  const subtotal = o.price * (o.quantity || 1);
                  return (
                    <label key={o.id} data-testid={`pick-order-${o.id}`} className="flex items-center gap-3 p-2 border-2 border-brand-ink cursor-pointer hover:bg-brand-yellow">
                      <input
                        type="checkbox"
                        checked={!!selected[o.id]}
                        onChange={(e) => setSelected({ ...selected, [o.id]: e.target.checked })}
                        className="w-5 h-5 accent-black"
                      />
                      <div className="flex-1">
                        <p className="font-bold text-sm">{o.handle}</p>
                        <p className="text-xs text-brand-ink2">{o.quantity}x [{o.product_code}] {o.description}</p>
                      </div>
                      <p className="font-bold">{brl(subtotal)}</p>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <p className="font-bold">
                Selecionado: <span className={Math.abs(selectedSum - pickingFor.amount) < 0.01 ? "text-brand-success" : ""}>{brl(selectedSum)}</span>
                {" / "}
                <span>{brl(pickingFor.amount)}</span>
              </p>
              <button data-testid="confirm-match-btn" onClick={confirmMatch} className="btn-pink">
                <ClipboardText weight="bold" size={16} /> Confirmar baixa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* -------- Live Products Tab (scoped to the live's contractor) -------- */
const EMPTY_PROD = { code: "", description: "", department_id: "", price: "" };

function LiveProductsTab({ scopeContractor, products, departments, onChange }) {
  const [form, setForm] = useState(EMPTY_PROD);
  const [editing, setEditing] = useState(null);
  const scopeLabel = scopeContractor ? scopeContractor.name : "Nossa loja";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.description.trim()) return toast.error("Código e descrição são obrigatórios");
    const payload = {
      code: form.code.trim(),
      description: form.description.trim(),
      department_id: form.department_id || null,
      contractor_id: editing
        ? (products.find(p => p.id === editing)?.contractor_id || null)
        : (scopeContractor?.id || null),
      price: parseMoney(form.price),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Produto cadastrado");
      }
      setForm(EMPTY_PROD);
      setEditing(null);
      onChange();
    } catch { toast.error("Erro ao salvar produto"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este produto?")) return;
    await api.delete(`/products/${id}`);
    onChange();
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      code: p.code,
      description: p.description,
      department_id: p.department_id || "",
      price: String(p.price).replace(".", ","),
    });
  };

  return (
    <div className="space-y-6">
      <div className="card-brutal p-5 bg-brand-mint">
        <div className="flex items-center gap-2">
          <Package weight="bold" size={20} />
          <div>
            <h3 className="font-heading text-lg font-black uppercase tracking-wide">Produtos desta live</h3>
            <p className="text-xs text-brand-ink2">
              Escopo: <strong>{scopeLabel}</strong> · {products.length} produto{products.length === 1 ? "" : "s"}
              {scopeContractor && " · Peças recebidas do contratante"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-brutal p-5 grid grid-cols-1 md:grid-cols-[120px_1.4fr_1fr_140px_auto] gap-3 items-end">
        <div>
          <label className="label-brutal">Código</label>
          <input data-testid="live-prod-code" className="input-brutal" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="001" />
        </div>
        <div>
          <label className="label-brutal">Descrição</label>
          <input data-testid="live-prod-desc" className="input-brutal" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Anel Prata Turca" />
        </div>
        <div>
          <label className="label-brutal">Departamento</label>
          <select data-testid="live-prod-dept" className="input-brutal" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
            <option value="">—</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Preço (R$)</label>
          <MoneyInput data-testid="live-prod-price" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
        </div>
        <div className="flex gap-2">
          <button data-testid="live-prod-save" type="submit" className="btn-pink h-[52px]">
            {editing ? <><FloppyDisk weight="bold" size={16} />Salvar</> : <><Plus weight="bold" size={16} />Adicionar</>}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_PROD); }} className="btn-white h-[52px]">
              <XIcon weight="bold" size={16} />
            </button>
          )}
        </div>
      </form>

      {products.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Package weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhum produto vinculado</p>
          <p className="text-brand-ink2 text-sm">Cadastre acima os produtos {scopeContractor ? `enviados por ${scopeContractor.name}` : "da loja"}.</p>
        </div>
      ) : (
        <div className="card-brutal overflow-x-auto">
          <table className="tbl w-full">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Departamento</th>
                <th className="text-right">Preço</th>
                <th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`live-prod-row-${p.id}`}>
                  <td className="font-bold">{p.code}</td>
                  <td>{p.description}</td>
                  <td>{p.department_name || <span className="text-brand-ink2">—</span>}</td>
                  <td className="text-right font-bold">{brl(p.price)}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={`live-prod-edit-${p.id}`} onClick={() => startEdit(p)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow">
                        <PencilSimple weight="bold" size={14} />
                      </button>
                      <button data-testid={`live-prod-del-${p.id}`} onClick={() => remove(p.id)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                        <Trash weight="bold" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function OrderRow({ order, canWrite, onTogglePaid, onDelete, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(order.product_code || "");
  const [desc, setDesc] = useState(order.description || "");
  const [qty, setQty] = useState(String(order.quantity || 1));
  const [price, setPrice] = useState(String(order.price ?? 0).replace(".", ","));
  const [photoUrl, setPhotoUrl] = useState(() => {
    // Existing base64 photo (when list was fetched with_photos=1) OR the thumb URL
    // served by the dedicated endpoint (default in fast list). w=800 gives a nice
    // preview while editing without transferring the full-size base64.
    if (order.photo_url) return order.photo_url;
    if (order.has_photo) return `${process.env.REACT_APP_BACKEND_URL || ""}/api/orders/${order.id}/photo?w=800`;
    return "";
  });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileGalleryRef = useRef(null);
  const fileCameraRef = useRef(null);

  const compressImage = (file, maxSize = 1280, quality = 0.78) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio); height = Math.round(height * ratio);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setPhotoUrl(dataUrl);
    } catch { toast.error("Falha ao ler a imagem"); }
  };

  const analyzeAI = async () => {
    if (!photoUrl) return toast.error("Envie uma foto primeiro");
    if (analyzing) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/orders/analyze-photo", { image_base64: photoUrl });
      if (data?.description) setDesc(data.description);
      if (data?.suggested_code && !code.trim()) setCode(data.suggested_code);
      if (data?.estimated_price != null) {
        setPrice(String(data.estimated_price).replace(".", ","));
      }
      toast.success("IA preencheu os campos · revise antes de salvar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao analisar foto");
    } finally { setAnalyzing(false); }
  };

  const startEdit = () => {
    setCode(order.product_code || "");
    setDesc(order.description || "");
    setQty(String(order.quantity || 1));
    setPrice(String(order.price ?? 0).replace(".", ","));
    setPhotoUrl(order.photo_url || "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    if (saving) return;
    const priceTrim = String(price).trim();
    if (!priceTrim || !/\d/.test(priceTrim) || !/^-?\s*[\d.,]+$/.test(priceTrim)) {
      return toast.error("Informe um preço válido");
    }
    const parsedPrice = parseFloat(priceTrim.replace(/\./g, "").replace(",", "."));
    if (isNaN(parsedPrice) || parsedPrice < 0) return toast.error("Preço inválido");
    const parsedQty = parseInt(qty, 10);
    if (isNaN(parsedQty) || parsedQty < 1) return toast.error("Quantidade deve ser ≥ 1");
    if (!code.trim() || !desc.trim()) return toast.error("Preencha código e descrição");
    setSaving(true);
    try {
      const photoChanged = (photoUrl || "") !== (order.photo_url || "");
      const payload = {
        product_code: code.trim(),
        description: desc.trim(),
        price: parsedPrice,
        quantity: parsedQty,
      };
      if (photoChanged) payload.photo_url = photoUrl || "";
      await api.put(`/orders/${order.id}`, payload);
      toast.success("Pedido atualizado");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar");
    } finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") cancel();
  };

  if (editing) {
    return (
      <tr data-testid={`order-row-${order.id}`} className="bg-brand-cream">
        <td>
          <div className="flex items-center gap-1">
            <div className="relative w-9 h-9 border-2 border-brand-ink bg-white overflow-hidden shrink-0">
              {photoUrl ? (
                <img data-testid={`edit-photo-preview-${order.id}`} src={photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <Camera weight="bold" size={16} className="text-brand-ink2 m-auto absolute inset-0" />
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <input
                ref={fileGalleryRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`edit-photo-file-${order.id}`}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
              <input
                ref={fileCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid={`edit-photo-camera-${order.id}`}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileCameraRef.current?.click()}
                data-testid={`edit-photo-camera-btn-${order.id}`}
                title="Tirar foto com a câmera"
                className="p-0.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
              >
                <Camera weight="bold" size={10} />
              </button>
              <button
                type="button"
                onClick={() => fileGalleryRef.current?.click()}
                data-testid={`edit-photo-gallery-btn-${order.id}`}
                title={photoUrl ? "Trocar imagem" : "Escolher da galeria"}
                className="p-0.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
              >
                <UploadSimple weight="bold" size={10} />
              </button>
              <button
                type="button"
                onClick={analyzeAI}
                disabled={!photoUrl || analyzing}
                data-testid={`edit-photo-ai-btn-${order.id}`}
                title="Preencher com IA a partir da foto"
                className="p-0.5 border-2 border-brand-ink bg-brand-pink text-white hover:bg-brand-yellow hover:text-brand-ink disabled:opacity-40"
              >
                <Sparkle weight="bold" size={10} />
              </button>
              {photoUrl && (
                <button
                  type="button"
                  onClick={() => setPhotoUrl("")}
                  data-testid={`edit-photo-clear-btn-${order.id}`}
                  title="Remover foto"
                  className="p-0.5 border-2 border-brand-ink bg-white hover:bg-brand-peach"
                >
                  <XIcon weight="bold" size={10} />
                </button>
              )}
            </div>
          </div>
        </td>
        <td>
          <input
            data-testid={`edit-code-${order.id}`} autoFocus
            className="input-brutal !h-9 !py-1 text-sm w-full"
            value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={onKeyDown}
          />
        </td>
        <td>
          <input
            data-testid={`edit-desc-${order.id}`}
            className="input-brutal !h-9 !py-1 text-sm w-full"
            value={desc} onChange={(e) => setDesc(e.target.value)} onKeyDown={onKeyDown}
          />
        </td>
        <td className="text-right">
          <input
            data-testid={`edit-qty-${order.id}`} type="number" min="1"
            className="input-brutal !h-9 !py-1 text-sm w-16 text-right"
            value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={onKeyDown}
          />
        </td>
        <td className="text-right">
          <input
            data-testid={`edit-price-${order.id}`} inputMode="decimal"
            className="input-brutal !h-9 !py-1 text-sm w-24 text-right"
            value={price} onChange={(e) => setPrice(e.target.value)} onKeyDown={onKeyDown}
          />
        </td>
        <td className="text-right text-brand-ink2 text-sm">—</td>
        <td className="text-right"><span className="text-brand-ink2 text-xs">editando…</span></td>
        <td className="text-right">
          <div className="inline-flex gap-1">
            <button
              type="button" onClick={save} disabled={saving}
              data-testid={`save-order-${order.id}`}
              className="p-1.5 border-2 border-brand-ink bg-brand-mint hover:bg-brand-yellow disabled:opacity-60"
              title="Salvar"
            >
              <FloppyDisk weight="bold" size={12} />
            </button>
            <button
              type="button" onClick={cancel}
              data-testid={`cancel-order-${order.id}`}
              className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach"
              title="Cancelar"
            >
              <XIcon weight="bold" size={12} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid={`order-row-${order.id}`}>
      <td>
        {order.has_photo || order.photo_url ? (
          <LazyImage
            src={order.photo_thumb ? `${API_BASE}${order.photo_thumb}` : order.photo_url}
            alt=""
            imgTestid={`order-thumb-${order.id}`}
            className="w-9 h-9 border-2 border-brand-ink"
          />
        ) : <div className="w-9 h-9 border-2 border-brand-ink bg-brand-cream" />}
      </td>
      <td className="font-bold">{order.product_code}</td>
      <td>{order.description}</td>
      <td className="text-right">{order.quantity || 1}</td>
      <td className="text-right">{brl(order.price)}</td>
      <td className="text-right font-bold">{brl(order.price * (order.quantity || 1))}</td>
      <td className="text-right">
        {order.withdrawn ? (
          <span className="badge-brutal bg-brand-peach text-brand-ink" title={order.withdrawn_reason ? `Motivo: ${order.withdrawn_reason}` : "Desistência / bloqueio"}>
            <XIcon weight="bold" size={12} /> Desistência{order.withdrawn_reason ? ` · ${order.withdrawn_reason}` : ""}
          </span>
        ) : order.paid ? (
          <button
            data-testid={`toggle-paid-${order.id}`}
            onClick={() => onTogglePaid(order)}
            disabled={!canWrite}
            className={`badge-brutal bg-brand-success text-white ${canWrite ? "" : "opacity-60 cursor-not-allowed"}`}
            title={order.payment_method ? `Pago via ${METHOD_LABEL[order.payment_method] || order.payment_method}` : "Pago"}
          >
            <Check weight="bold" size={12} /> Pago{order.payment_method ? ` · ${METHOD_LABEL[order.payment_method] || order.payment_method}` : ""}
          </button>
        ) : canWrite ? (
          <PaymentMethodMenu
            testid={`toggle-paid-${order.id}`}
            label="Marcar pago"
            variant="white"
            size="sm"
            onSelect={(m) => onTogglePaid(order, m)}
          />
        ) : (
          <span className="badge-brutal bg-brand-yellow opacity-60">Pendente</span>
        )}
      </td>
      <td className="text-right">
        {canWrite && (
          <div className="inline-flex gap-1">
            <button
              data-testid={`edit-order-${order.id}`}
              onClick={startEdit}
              title="Editar pedido"
              className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
            >
              <PencilSimple weight="bold" size={12} />
            </button>
            <button
              data-testid={`delete-order-${order.id}`}
              onClick={() => onDelete(order)}
              title="Excluir"
              className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach"
            >
              <Trash weight="bold" size={12} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
